import type { GbpCategoryOption, GbpEditField, GbpEditValue } from "../../../../services/gbpService";
import { isRecord, namesList, plural } from "../../../../features/analytics/profileFindingsModel";
import type { ApplyItem, EditOutcome, GbpProposalAttachment } from "./gbpProposalText";
import { describeGbpProposal, formatValue, isHours, isOpeningDate, isSettled, liveProgress } from "./gbpProposalText";

/**
 * The Visibility Manager's Google Business Profile proposal, v2 (SGL-421), as
 * data. Pure: no React, no fetching.
 *
 * chat-service builds the plan (app/gbp_plan): local SEO rules decide which rows
 * exist, one model call writes the values and reasons, a validator keeps only
 * what is sourced. The card shows it as an ordered list; the owner approves,
 * skips or edits each row, or approves all. Every approval is still one edit
 * through social-management's /gbp/edits, logged in GbpEditLog there, so the
 * apply, status and history machinery of the v1 card is reused as is: a row's
 * outcome lives in `applied[row.id]`.
 */

/* ── the plan, as chat-service sends it ────────────────────────────────── */

export type PlanSourceName =
  | "profile"
  | "brand"
  | "knowledge"
  | "search_console"
  | "visibility_check"
  | "attributes"
  | "google_categories";

export interface PlanSource {
  /** ok | none | not_connected | needs_reauth | failed */
  status: string;
  detail?: string;
}

export interface PlanEvidence {
  source: string;
  text: string;
  ref?: string;
}

export type PlanRowKind = "edit" | "ask" | "manual" | "photo" | "handoff";
export type PlanSection = "fix" | "optional" | "team";

export interface PlanAttributeOption {
  value: boolean | string;
  display_name: string | null;
}

export interface GbpPlanRow {
  id: string;
  group: string;
  kind: PlanRowKind;
  section: PlanSection;
  label: string;
  reason: string;
  evidence: PlanEvidence[];
  /** ready | needs_input | not_drafted */
  status: string;
  field?: GbpEditField | "attributes";
  before?: unknown;
  value?: GbpEditValue | null;
  display?: unknown;
  questions?: string[];
  meta?: {
    service?: string;
    description?: string;
    not_drafted?: string;
    changes_primary?: boolean;
    attribute?: string;
    group?: string | null;
    value_type?: "BOOL" | "ENUM";
    options?: PlanAttributeOption[];
    photo_count?: number;
    newest?: string | null;
    agent?: string;
    /** The owner must check this before it goes to Google (a phone that differs
     *  from Brand details, a service nobody told us they offer). Never on Approve all. */
    confirm?: string;
  };
}

/** chat-service gbp_plan.google_settings. confirmed: the owner said they're off
 *  under a week ago, nothing to ask. recheck: a week or more ago, ask once
 *  "still off?". unconfirmed: never said, said back on, or Google is editing. */
export interface GbpGoogleSettings {
  state: "confirmed" | "recheck" | "unconfirmed";
  confirmed_at: string | null;
  via?: string;
  read_failed?: boolean;
}

export interface GbpPlan {
  version: 2;
  /** ok | not_connected | needs_reauth | failed */
  status: string;
  detail?: string;
  location_id?: string;
  title?: string | null;
  has_storefront?: boolean;
  rows: GbpPlanRow[];
  counts?: { fixes: number; ready: number; needs_input: number; team: number; optional: number };
  hidden_walk_in?: number;
  /** Google set something to match its own suggestion (Step 2, SGL-421), so its automatic updates are likely on. */
  has_google_updated?: boolean;
  /** The fields Google changed on its own (getGoogleUpdated's diffMask): [] none, null when Google couldn't be asked. */
  google_changed?: string[] | null;
  /** The owner's last word on Google's automatic updates, from an earlier card in any chat (SGL-421). */
  google_settings?: GbpGoogleSettings;
  summary?: string[];
  writer?: PlanSource;
  sources?: Partial<Record<PlanSourceName, PlanSource>>;
  built_at?: string;
}

/** The plan from a response or an SSE event, or null when it isn't one. */
export function readPlan(raw: unknown): GbpPlan | null {
  if (!isRecord(raw) || raw.version !== 2 || typeof raw.status !== "string" || !Array.isArray(raw.rows)) return null;
  const rows = raw.rows.filter(
    (r): r is GbpPlanRow =>
      isRecord(r) &&
      typeof r.id === "string" &&
      typeof r.label === "string" &&
      typeof r.kind === "string" &&
      typeof r.section === "string" &&
      typeof r.reason === "string",
  );
  return { ...(raw as unknown as GbpPlan), rows: rows.map((r) => ({ ...r, evidence: Array.isArray(r.evidence) ? r.evidence : [] })) };
}

/* ── words ─────────────────────────────────────────────────────────────── */

export const SOURCE_NAMES: Record<PlanSourceName, string> = {
  profile: "Google profile",
  brand: "Brand details",
  knowledge: "Knowledge files",
  search_console: "Search Console",
  visibility_check: "AI visibility check",
  attributes: "Google's optional details",
  google_categories: "Google's categories",
};

export const EVIDENCE_NAMES: Record<string, string> = {
  brand: "Brand details",
  knowledge: "Knowledge file",
  search_console: "Search Console",
  visibility_check: "AI visibility check",
  profile: "Your Google profile",
  google_categories: "Google's categories",
  local_seo: "Local search practice",
};

/** Sources that could not be read, for the card's warning line. "none" and
 *  "not_connected" are product states, said only when they limited the plan. */
export function sourceProblems(plan: GbpPlan): Array<{ name: string; detail: string; failed: boolean }> {
  const out: Array<{ name: string; detail: string; failed: boolean }> = [];
  for (const [key, src] of Object.entries(plan.sources ?? {}) as Array<[PlanSourceName, PlanSource]>) {
    if (!src || src.status === "ok" || key === "profile") continue;
    if (src.status === "none" && key === "visibility_check") continue;
    out.push({ name: SOURCE_NAMES[key] ?? key, detail: src.detail || "", failed: src.status === "failed" });
  }
  if (plan.writer && plan.writer.status !== "ok") {
    out.push({ name: "Writing", detail: plan.writer.detail || "Some text couldn't be written.", failed: true });
  }
  if (plan.has_google_updated && plan.google_changed === null) {
    out.push({ name: "Google's own changes", detail: "Google changed something on its own, but couldn't say what just now.", failed: true });
  }
  return out;
}

/** What Google shows now / what it becomes, as text. */
export function shown(v: unknown): string {
  if (v == null || v === "") return "";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) {
    return v
      .map((x) => (typeof x === "string" ? x : isRecord(x) ? String(x.name ?? x.display_name ?? "") : ""))
      .filter(Boolean)
      .join(", ");
  }
  if (isRecord(v) && typeof v.display_name === "string") return v.display_name;
  return formatValue(v as GbpEditValue);
}

/* ── the owner's state on a row ────────────────────────────────────────── */

export const rowsIn = (plan: GbpPlan, section: PlanSection) => plan.rows.filter((r) => r.section === section);

/** The value an approval sends: the owner's edit if there is one, else the plan's. */
export function rowValue(row: GbpPlanRow, card: GbpProposalAttachment): GbpEditValue | null {
  // A main category the owner picked is kept with its display name (primaryAnswer,
  // as on the v1 card); the edit takes its resource name.
  if (row.field === "primary_category" && row.kind === "ask") return card.primaryAnswer?.name ?? null;
  const answer = card.planAnswers?.[row.id];
  if (answer !== undefined && answer !== null && answer !== "") return answer;
  return row.value ?? null;
}

export const outcomeOf = (row: GbpPlanRow, card: GbpProposalAttachment): EditOutcome | null =>
  card.applied?.[row.id] ?? null;

/** Settled on Google, skipped, or marked done: nothing left to do on the row. */
export function isClosed(row: GbpPlanRow, card: GbpProposalAttachment): boolean {
  return isSettled(outcomeOf(row, card)) || !!card.skipped?.[row.id] || !!card.doneOnGoogle?.[row.id];
}

function validValue(field: GbpPlanRow["field"], v: GbpEditValue | null): boolean {
  if (v == null) return false;
  if (field === "regular_hours") return isHours(v);
  if (field === "opening_date") return isOpeningDate(v);
  if (field === "attributes") return isRecord(v) && typeof v.attribute === "string" && v.value !== undefined && v.value !== null;
  if (field === "services" || field === "additional_categories") return Array.isArray(v) && v.length > 0;
  return typeof v === "string" && v.trim().length > 0;
}

/** The edit this row sends right now, or null (nothing to send yet, or not an edit). */
export function applyItemFor(row: GbpPlanRow, card: GbpProposalAttachment): ApplyItem | null {
  if (row.kind === "photo") {
    const pick = card.photo;
    return pick?.category ? { key: row.id, field: "photo", photo: { sourceUrl: pick.sourceUrl, category: pick.category } } : null;
  }
  if ((row.kind !== "edit" && row.kind !== "ask") || !row.field) return null;
  const value = rowValue(row, card);
  if (!validValue(row.field, value)) return null;
  return {
    key: row.id,
    field: row.field as GbpEditField,
    value: value as GbpEditValue,
    ...(row.questions?.length ? { questions: row.questions } : {}),
  };
}

/** The owner gave this row its value themselves: typed or edited it, picked a
 *  main category, or chose a photo. */
export function ownerGave(row: GbpPlanRow, card: GbpProposalAttachment): boolean {
  if (row.kind === "photo") return !!card.photo?.category;
  if (row.field === "primary_category" && row.kind === "ask") return !!card.primaryAnswer;
  const answer = card.planAnswers?.[row.id];
  return answer !== undefined && answer !== null && answer !== "";
}

/** Approve all may send this row: a prepared edit the plan calls ready and the
 *  owner need not check first, or a value the owner gave. A service with no
 *  description, a service only a lost search suggested, or a phone that differs
 *  from Brand details is approved on its own, never in bulk. */
export function inApproveAll(row: GbpPlanRow, card: GbpProposalAttachment): boolean {
  if (ownerGave(row, card)) return true;
  return row.kind === "edit" && row.status === "ready" && !row.meta?.confirm;
}

/** The rows "Approve all" sends: every fix ready to go that the owner has not
 *  skipped and Google has not settled. Optional details never ride along. */
export function approveAllItems(plan: GbpPlan, card: GbpProposalAttachment): ApplyItem[] {
  return rowsIn(plan, "fix")
    .filter((r) => !isClosed(r, card) && outcomeOf(r, card)?.status !== "applying" && inApproveAll(r, card))
    .map((r) => applyItemFor(r, card))
    .filter((x): x is ApplyItem => x !== null);
}

/** Fixes still waiting on the owner: an answer only they can give, or a manual step. */
export const openFixes = (plan: GbpPlan, card: GbpProposalAttachment) =>
  rowsIn(plan, "fix").filter((r) => !isClosed(r, card));

export const isCategoryOption = (v: unknown): v is GbpCategoryOption =>
  isRecord(v) && typeof v.name === "string" && typeof v.display_name === "string";

/* ── the card as text, for the model ───────────────────────────────────── */

const SUMMARY_MAX = 5_800; // under POST /visibility/gbp-proposal's 6,000

function outcomeWords(o: EditOutcome | null): string {
  switch (o?.status) {
    case "live": return "live on Google";
    case "pending_review": return "sent, waiting for Google to publish";
    case "applied_unverified": return "sent, not confirmed yet";
    case "submitted": return "sent to Google";
    case "no_answer": return "sent, no answer from Google yet";
    case "not_on_google": return "sent, but not showing on Google";
    case "failed": return `not changed (${o.message || "Google refused it"})`;
    case "applying": return "being sent";
    default: return "";
  }
}

/** The v2 card as the agent reads it back: every row, what the owner did with
 *  it, and exactly what reached Google, so the agent never claims more. */
export function describePlan(card: GbpProposalAttachment): string {
  const plan = card.plan;
  if (card.loading) return "[Preparing the owner's Google Business Profile proposal.]";
  if (card.planError) return `[Couldn't prepare the Google Business Profile proposal: ${card.planError}]`;
  if (!plan) return "[No Google Business Profile proposal.]";
  if (plan.status !== "ok") return `[Couldn't read the Google Business Profile: ${plan.detail || plan.status}.]`;
  const lines = [`[Showed the owner a prepared Google Business Profile proposal. ${(plan.summary ?? []).join(" ")}`];
  for (const r of plan.rows) {
    if (r.section === "optional" && !card.applied?.[r.id]) continue;
    const value = shown(rowValue(r, card) ?? r.display);
    const viaChat = card.revisedInChat?.[r.id] ? ", changed in chat" : "";
    const state = card.skipped?.[r.id]
      ? "skipped by the owner"
      : card.doneOnGoogle?.[r.id]
        ? "owner marked it done on Google"
        : outcomeWords(outcomeOf(r, card)) || (r.meta?.confirm ? "waiting for the owner to check it" : r.status === "needs_input" ? "waiting for the owner's answer" : "not sent yet");
    lines.push(`${r.label}${value ? `: ${value.length > 160 ? `${value.slice(0, 160)}…` : value}` : ""} (${state}${viaChat}).`);
  }
  const optional = rowsIn(plan, "optional").length;
  if (optional) lines.push(`${plural(optional, "optional detail", "optional details")} offered, collapsed.`);
  if (plan.hidden_walk_in) lines.push(`${plural(plan.hidden_walk_in, "walk-in question", "walk-in questions")} hidden: no storefront.`);
  const progress = liveProgress(card.applied);
  lines.push(
    progress.total === 0
      ? "Nothing has been changed on Google yet.]"
      : "Only the rows marked sent above went to Google; nothing else was changed.]",
  );
  const text = lines.join(" ");
  return text.length > SUMMARY_MAX ? `${text.slice(0, SUMMARY_MAX - 2)}…]` : text;
}

/** The card as text, either version. */
export const describeCard = (card: GbpProposalAttachment): string =>
  card.plan || card.planError ? describePlan(card) : describeGbpProposal(card);

/* ── the agent working on the card from chat (SGL-421) ─────────────────── */

/** One change the Visibility Manager made to a row for the owner, from chat
 *  (chat-service revise_gbp_row, its `gbp_row_update` event). Checked there;
 *  the card applies it as if the owner had done it, and nothing reaches Google
 *  until they approve. */
export interface GbpRowUpdate {
  cardMessageSk: string | null;
  rowId: string;
  /** approve / approve_all: the owner told the agent to send it; the card sends it as its Approve button does.
   *  auto_off: the owner told the agent they turned off Google's automatic updates ("I've turned them off"). */
  action: "answer" | "confirm" | "skip" | "unskip" | "done" | "approve" | "approve_all" | "auto_off";
  value?: GbpEditValue;
}

const ROW_UPDATE_ACTIONS = new Set(["answer", "confirm", "skip", "unskip", "done", "approve", "approve_all", "auto_off"]);

// The words an owner uses for their Business Profile ("fix my gbp", "my Google listing").
export const GBP_REQUEST = /\b(gbp|gmb|google\s+(my\s+)?business(\s+profile)?|business\s+profile|google\s+(maps\s+)?listing|google\s+maps\s+profile)\b/i;


/** The update from an SSE event, or null when it isn't one. */
export function readRowUpdate(raw: unknown): GbpRowUpdate | null {
  if (!isRecord(raw) || typeof raw.row_id !== "string" || typeof raw.action !== "string") return null;
  if (!ROW_UPDATE_ACTIONS.has(raw.action)) return null;
  if (raw.action === "answer" && (raw.value === undefined || raw.value === null)) return null;
  return {
    cardMessageSk: typeof raw.card_message_sk === "string" ? raw.card_message_sk : null,
    rowId: raw.row_id,
    action: raw.action as GbpRowUpdate["action"],
    ...(raw.action === "answer" ? { value: raw.value as GbpEditValue } : {}),
  };
}

/* ── Google's automatic updates, remembered per profile (SGL-421) ──────── */

/** A new card starts with the settings off when the owner said so on an
 *  earlier card, in any chat, under a week ago and Google hasn't edited since. */
export const settingsAlreadyOff = (plan: GbpPlan): boolean => plan.google_settings?.state === "confirmed";

/** When the owner said they were off, if the card should ask once whether they
 *  still are (a week or more ago); null otherwise. Gone once they answer, and
 *  when an approval was stopped because Google is editing (the full box shows). */
export function settingsRecheckSince(card: GbpProposalAttachment): string | null {
  const settings = card.plan?.google_settings;
  if (settings?.state !== "recheck" || !settings.confirmed_at) return null;
  if (card.googleAutoOff || card.googleBackOn || Array.isArray(card.googleUpdatedNow)) return null;
  return settings.confirmed_at;
}

/** What the owner just told the card about the settings: off, back on, or
 *  nothing (null). Saving it lets later cards skip the question. */
export function settingsAnswer(before: GbpProposalAttachment, after: GbpProposalAttachment): boolean | null {
  if (!before.googleAutoOff && after.googleAutoOff) return true;
  if (!before.googleBackOn && after.googleBackOn) return false;
  return null;
}

/** What the card sends when the owner told the agent to approve in chat: the
 *  same items its Approve / Approve all buttons would send, read from the card
 *  as it is now (a change made earlier in the same reply included). Nothing
 *  until the owner has confirmed Google's automatic updates are off, the same
 *  gate the buttons have, and never a row already sent or being sent. */
export function approvalItems(card: GbpProposalAttachment, update: GbpRowUpdate): ApplyItem[] {
  const plan = card.plan;
  if (!plan || plan.status !== "ok" || !card.googleAutoOff) return [];
  if (update.action === "approve_all") return approveAllItems(plan, card);
  if (update.action !== "approve") return [];
  const row = plan.rows.find((r) => r.id === update.rowId);
  if (!row || isClosed(row, card) || card.skipped?.[row.id]) return [];
  const outcome = outcomeOf(row, card);
  if (outcome && (isSettled(outcome) || outcome.status === "applying")) return [];
  const item = applyItemFor(row, card);
  return item ? [item] : [];
}

/** The card with the update applied, or null when the row isn't on it or Google
 *  already has it (the agent's view may lag a click the owner made since). */
export function applyRowUpdate(card: GbpProposalAttachment, update: GbpRowUpdate): GbpProposalAttachment | null {
  // Card-level: the same as the owner pressing "I've turned them off".
  if (update.action === "auto_off") return card.plan ? { ...card, googleAutoOff: true, googleUpdatedNow: undefined } : null;
  const row = card.plan?.rows.find((r) => r.id === update.rowId);
  if (!row) return null;
  const outcome = outcomeOf(row, card);
  if (outcome && (isSettled(outcome) || outcome.status === "applying")) return null;
  const id = row.id;
  const revisedInChat = { ...(card.revisedInChat ?? {}), [id]: true };
  switch (update.action) {
    case "answer":
      return {
        ...card,
        planAnswers: { ...(card.planAnswers ?? {}), [id]: update.value as GbpEditValue },
        skipped: { ...(card.skipped ?? {}), [id]: false },
        revisedInChat,
      };
    case "confirm": {
      // The owner's yes to what the row shows now: their own value if they (or
      // the agent, earlier in this turn) changed it, else the plan's.
      const value = rowValue(row, card);
      if (value === null || value === undefined) return null;
      return {
        ...card,
        planAnswers: { ...(card.planAnswers ?? {}), [id]: value },
        skipped: { ...(card.skipped ?? {}), [id]: false },
        revisedInChat,
      };
    }
    case "skip":
      return { ...card, skipped: { ...(card.skipped ?? {}), [id]: true }, revisedInChat };
    case "unskip":
      return { ...card, skipped: { ...(card.skipped ?? {}), [id]: false }, revisedInChat };
    case "done":
      return { ...card, doneOnGoogle: { ...(card.doneOnGoogle ?? {}), [id]: true }, revisedInChat };
    default:
      return null;
  }
}

/** What the owner's approvals did on Google, in plain words: counts, then the
 *  changes Google refused by name (each row shows Google's reason). Empty
 *  before anything was sent. */
export function outcomeReport(card: GbpProposalAttachment): string[] {
  const counts = { live: 0, waiting: 0, gone: 0 };
  const refused: string[] = [];
  for (const r of card.plan?.rows ?? []) {
    const o = outcomeOf(r, card);
    if (!o || o.status === "applying") continue;
    if (o.status === "live") counts.live += 1;
    else if (o.status === "failed") refused.push(r.label);
    else if (o.status === "not_on_google") counts.gone += 1;
    else counts.waiting += 1;
  }
  const lines: string[] = [];
  if (counts.live) lines.push(`${plural(counts.live, "change is", "changes are")} live on Google.`);
  if (counts.waiting) lines.push(`${plural(counts.waiting, "change is", "changes are")} waiting for Google to publish. I'll keep checking.`);
  if (counts.gone) lines.push(`${plural(counts.gone, "change doesn't", "changes don't")} show on Google any more. Google may have replaced it.`);
  if (refused.length) lines.push(`Google refused ${namesList(refused)}. The reason is on each one.`);
  return lines;
}
